import React, { useMemo, useState } from 'react';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import {
    Box,
    Typography,
    Button,
    Container,
    Modal,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import { Add, Close, Remove } from '@mui/icons-material';
import { strings } from '../../../constants';
import { FilterItem, filters, mainCategorySelector } from '../../../data/filters/discovery.data';
import { useDispatch, useSelector } from 'react-redux';
import {
    currentFilterCategorySelector,
    filterSubCategoryIdSelector,
} from '../../../selectors/selectors';
import { FilterValueSet } from '../../../redux/appConfigurator/types';
import { clearFilterSubCategory, toggleFiler } from '../../../redux/appConfigurator/action';
import TuneIcon from '@mui/icons-material/Tune';

interface IFilterProps {
    open?: boolean;
    width?: number | string;
    onChangeFilterCategory: (filter: FilterItem) => void;
    onChangeFilterSubCategory: (filter: FilterItem, categoryId: number) => void;
    filterList: any[];
    filterSelections: FilterValueSet;
    onClearFilterPressed: () => void;
    onApplyFilterPressed: () => void;
    disableApplyFilter?: boolean;
}

export interface IFilterRef {
    openMainCatgeory(): void;
    openSubCatgeory(): void;
}

const EsFiltersDesktop: React.FC<IFilterProps> = ({
    open,
    onChangeFilterCategory,
    onChangeFilterSubCategory,
    filterSelections,
    onClearFilterPressed,
    onApplyFilterPressed,
    disableApplyFilter,
}) => {
    const { themeStyle } = useThemeContext();
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [expandedSection, setExpandedSection] = useState<string | null>(null);
    const selectedCategory = useSelector(currentFilterCategorySelector);
    const selectedFilterSubCategoryId = useSelector(filterSubCategoryIdSelector);
    const dispatch = useDispatch();
    const campaignFilterList = useMemo(() => filters.campaigns, []);

    const categorySectionId = 'main-category';

    const sectionData = useMemo(
        () => [
            { id: categorySectionId, title: 'Category' },
            ...campaignFilterList.map(f => ({ id: String(f.id), title: f.categoryName })),
        ],
        [campaignFilterList],
    );

    const handleFilterToggle = (filter: FilterItem) => {
        onChangeFilterCategory && onChangeFilterCategory(filter);
        dispatch(
            toggleFiler({
                filterStatus: 'off',
                filterId: undefined,
            }),
        );
    };

    const handleFilterSubCategory = (filter: FilterItem, categoryId: number) => {
        onChangeFilterSubCategory(filter, categoryId);
        dispatch(
            toggleFiler({
                filterStatus: 'off',
                filterId: selectedFilterSubCategoryId === categoryId ? undefined : categoryId,
            }),
        );
    };

    const handleOpenModal = () => setIsFilterModalOpen(true);
    const handleCloseModal = () => {
        setExpandedSection(null);
        setIsFilterModalOpen(false);
    };

    return (
        <Container sx={{ mt: 10 }}>
            <Box
                sx={{
                    height: '78px',
                    backgroundColor: theme[themeStyle].pure_black,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    px: 2,
                    zIndex: 999,
                }}>
                <Button
                    onClick={handleOpenModal}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        borderRadius: 999,
                        textTransform: 'none',
                        px: 2.5,
                        py: 1.2,
                        minWidth: 'fit-content',
                        backgroundColor: theme[themeStyle].true_highlight,
                        color: theme[themeStyle].pure_black,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 14,
                        fontWeight: 700,
                        '&:hover': {
                            backgroundColor: theme[themeStyle].true_highlight,
                            opacity: 0.92,
                        },
                    }}>
                    <TuneIcon sx={{ color: theme[themeStyle].pure_black, fontSize: 20 }} />
                    Filter
                </Button>
            </Box>

            <Modal open={isFilterModalOpen} onClose={handleCloseModal}>
                <Box
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: theme[themeStyle].pure_black,
                        color: theme[themeStyle].tag_white,
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                    <Box
                        sx={{
                            height: 72,
                            px: 3,
                            borderBottom: '1px solid rgba(255,255,255,0.06)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}>
                        <Button
                            onClick={handleCloseModal}
                            sx={{ minWidth: 0, p: 0, color: theme[themeStyle].tag_white, textTransform: 'none' }}>
                            <Close fontSize="small" />
                        </Button>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16, letterSpacing: '0.08em' }}>
                            FILTERS
                        </Typography>
                        <Button
                            onClick={onClearFilterPressed}
                            sx={{
                                minWidth: 0,
                                p: 0,
                                textTransform: 'uppercase',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 12,
                                color: theme[themeStyle].true_highlight,
                                letterSpacing: '0.06em',
                            }}>
                            Clear all
                        </Button>
                    </Box>

                    <Box sx={{ px: 2, py: 2, overflowY: 'auto', flex: 1 }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: theme[themeStyle].tag_white,
                                fontSize: 16,
                                mb: 2,
                            }}>
                            Filters
                        </Typography>

                        {sectionData.map(section => {
                            const isExpanded = expandedSection === section.id;
                            const selectedDisplay =
                                section.id === categorySectionId
                                    ? selectedCategory
                                    : filterSelections[Number(section.id)]?.displayName;

                            return (
                                <Accordion
                                    key={section.id}
                                    expanded={isExpanded}
                                    onChange={() => setExpandedSection(isExpanded ? null : section.id)}
                                    disableGutters
                                    elevation={0}
                                    sx={{
                                        backgroundColor: theme[themeStyle].deep_grey,
                                        mb: 1,
                                        '&:before': { display: 'none' },
                                    }}>
                                    <AccordionSummary
                                        expandIcon={
                                            isExpanded ? (
                                                <Remove sx={{ color: theme[themeStyle].tag_white }} />
                                            ) : (
                                                <Add sx={{ color: theme[themeStyle].tag_white }} />
                                            )
                                        }
                                        sx={{ minHeight: 56 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    color: theme[themeStyle].tag_white,
                                                    fontSize: 13,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.04em',
                                                }}>
                                                {section.title}
                                            </Typography>
                                            {selectedDisplay ? (
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        color: theme[themeStyle].true_highlight,
                                                        fontSize: 12,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                    {String(selectedDisplay)}
                                                </Typography>
                                            ) : null}
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                                        {section.id !== categorySectionId && (
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                                <Button
                                                    onClick={() =>
                                                        dispatch(clearFilterSubCategory({ id: Number(section.id) }))
                                                    }
                                                    sx={{
                                                        textTransform: 'none',
                                                        minWidth: 0,
                                                        px: 1.1,
                                                        py: 0.4,
                                                        borderRadius: 0,
                                                        border: `1px solid ${theme[themeStyle].tag_white}`,
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        fontSize: 9,
                                                        '&:hover': {
                                                            borderColor: theme[themeStyle].true_highlight,
                                                            color: theme[themeStyle].true_highlight,
                                                            backgroundColor: 'transparent',
                                                        },
                                                    }}>
                                                    {`Clear ${section.title} Filter`}
                                                </Button>
                                            </Box>
                                        )}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            {section.id === categorySectionId
                                                ? mainCategorySelector[0].data.list.map(option => {
                                                      const selected =
                                                          String(selectedCategory) === String(option.value);
                                                      return (
                                                          <Button
                                                              key={`${section.id}-${option.displayName}`}
                                                              onClick={() => handleFilterToggle(option)}
                                                              sx={{
                                                                  justifyContent: 'flex-start',
                                                                  textTransform: 'capitalize',
                                                                  fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                  color: selected
                                                                      ? theme[themeStyle].true_highlight
                                                                      : theme[themeStyle].tag_white,
                                                                  border: selected
                                                                      ? `1px solid ${theme[themeStyle].true_highlight}`
                                                                      : '1px solid rgba(255,255,255,0.08)',
                                                                  backgroundColor: selected
                                                                      ? 'rgba(255, 156, 40, 0.08)'
                                                                      : 'transparent',
                                                                  borderRadius: 1,
                                                                  py: 1,
                                                              }}>
                                                              {option.displayName}
                                                          </Button>
                                                      );
                                                  })
                                                : campaignFilterList
                                                      .find(f => String(f.id) === section.id)
                                                      ?.data.list.map(option => {
                                                          const selected =
                                                              String(
                                                                  filterSelections[Number(section.id)]?.displayName ??
                                                                      '',
                                                              ).toLowerCase() === option.displayName.toLowerCase();
                                                          return (
                                                              <Button
                                                                  key={`${section.id}-${option.displayName}`}
                                                                  onClick={() =>
                                                                      handleFilterSubCategory(
                                                                          option,
                                                                          Number(section.id),
                                                                      )
                                                                  }
                                                                  sx={{
                                                                      justifyContent: 'flex-start',
                                                                      textTransform: 'capitalize',
                                                                      fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                      color: selected
                                                                          ? theme[themeStyle].true_highlight
                                                                          : theme[themeStyle].tag_white,
                                                                      border: selected
                                                                          ? `1px solid ${theme[themeStyle].true_highlight}`
                                                                          : '1px solid rgba(255,255,255,0.08)',
                                                                      backgroundColor: selected
                                                                          ? 'rgba(255, 156, 40, 0.08)'
                                                                          : 'transparent',
                                                                      borderRadius: 1,
                                                                      py: 1,
                                                                  }}>
                                                                  {option.displayName}
                                                              </Button>
                                                          );
                                                      })}
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}
                    </Box>

                    <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                        <Button
                            fullWidth
                            onClick={() => {
                                onApplyFilterPressed();
                                handleCloseModal();
                            }}
                            disabled={disableApplyFilter}
                            sx={{
                                borderRadius: 0.75,
                                py: 1.2,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                textTransform: 'none',
                                backgroundColor: disableApplyFilter
                                    ? theme[themeStyle].disabledGray
                                    : theme[themeStyle].true_highlight,
                                color: theme[themeStyle].pure_black,
                                '&:hover': {
                                    backgroundColor: disableApplyFilter
                                        ? theme[themeStyle].disabledGray
                                        : theme[themeStyle].true_highlight,
                                    opacity: disableApplyFilter ? 1 : 0.92,
                                },
                            }}>
                            {strings.apply_filter}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Container>
    );
};

export { EsFiltersDesktop };
